function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

const color6Regexp = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const color3Regexp = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const rgbaRegexp = /^rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\s*\)$/i;
const parse = color => {
  if (typeof color !== "string") {
    throw new Error(`Failed to parse color: ${color}`);
  }
  const color3Match = color.match(color3Regexp);
  if (color3Match) {
    return {
      r: parseInt(`${color3Match[1]}${color3Match[1]}`, 16),
      g: parseInt(`${color3Match[2]}${color3Match[2]}`, 16),
      b: parseInt(`${color3Match[3]}${color3Match[3]}`, 16),
      a: 1
    };
  }
  const color6Match = color.match(color6Regexp);
  if (color6Match) {
    return {
      r: parseInt(color6Match[1], 16),
      g: parseInt(color6Match[2], 16),
      b: parseInt(color6Match[3], 16),
      a: 1
    };
  }
  const rgbaMatch = color.match(rgbaRegexp);
  if (rgbaMatch) {
    return {
      r: parseFloat(rgbaMatch[1]),
      g: parseFloat(rgbaMatch[2]),
      b: parseFloat(rgbaMatch[3]),
      a: rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1
    };
  }
  throw new Error(`Failed to parse color: ${color}`);
};
const format = color => {
  const r = Math.round(color.r);
  const g = Math.round(color.g);
  const b = Math.round(color.b);
  if (color.a === 1) {
    const _s = c => {
      const asString = c.toString(16);
      return asString.length === 1 ? `0${asString}` : asString;
    };
    return `#${_s(r)}${_s(g)}${_s(b)}`;
  } else {
    return `rgba(${r},${g},${b},${color.a.toFixed(2)})`;
  }
};
const fade = (color, percentage) => {
  if (percentage < 0 || percentage > 1) {
    throw new Error("Percentage must be between 0 and 1");
  }
  const components = parse(color);
  return format(_extends({}, components, {
    a: components.a * percentage
  }));
};
const mix = (color, background) => {
  const colorObj = parse(color);
  const bgObj = parse(background);
  return format({
    r: colorObj.r * colorObj.a + bgObj.r * (1 - colorObj.a),
    g: colorObj.g * colorObj.a + bgObj.g * (1 - colorObj.a),
    b: colorObj.b * colorObj.a + bgObj.b * (1 - colorObj.a),
    a: bgObj.a
  });
};

const offBlack = "#21242c";
const white = "#ffffff";
const eggplant = "#5f1e5c";
const baseColors = {
  blue: "#1865f2",
  purple: "#9059ff",
  green: "#00a60e",
  gold: "#ffb100",
  red: "#d92916",
  offBlack,
  offBlack64: fade(offBlack, 0.64),
  offBlack50: fade(offBlack, 0.5),
  offBlack32: fade(offBlack, 0.32),
  offBlack16: fade(offBlack, 0.16),
  offBlack8: fade(offBlack, 0.08),
  offWhite: "#f7f8fa",
  white,
  white64: fade(white, 0.64),
  white50: fade(white, 0.5),
  white32: fade(white, 0.32),
  darkBlue: "#0b2149",
  teal: "#14bf96"
};
const fadedColorWithWhite = (color, alpha) => mix(fade(color, alpha), baseColors.white);
const color = _extends({}, baseColors, {
  activeBlue: mix(baseColors.offBlack32, baseColors.blue),
  fadedBlue: fadedColorWithWhite(baseColors.blue, 0.32),
  fadedBlue24: fadedColorWithWhite(baseColors.blue, 0.24),
  fadedBlue16: fadedColorWithWhite(baseColors.blue, 0.16),
  fadedBlue8: fadedColorWithWhite(baseColors.blue, 0.08),
  activeRed: mix(baseColors.offBlack32, baseColors.red),
  fadedRed: fadedColorWithWhite(baseColors.red, 0.32),
  fadedRed24: fadedColorWithWhite(baseColors.red, 0.24),
  fadedRed16: fadedColorWithWhite(baseColors.red, 0.16),
  fadedRed8: fadedColorWithWhite(baseColors.red, 0.08),
  fadedGreen24: fadedColorWithWhite(baseColors.green, 0.24),
  fadedGreen16: fadedColorWithWhite(baseColors.green, 0.16),
  fadedGreen8: fadedColorWithWhite(baseColors.green, 0.08),
  fadedGold24: fadedColorWithWhite(baseColors.gold, 0.24),
  fadedGold16: fadedColorWithWhite(baseColors.gold, 0.16),
  fadedGold8: fadedColorWithWhite(baseColors.gold, 0.08),
  fadedPurple24: fadedColorWithWhite(baseColors.purple, 0.24),
  fadedPurple16: fadedColorWithWhite(baseColors.purple, 0.16),
  fadedPurple8: fadedColorWithWhite(baseColors.purple, 0.08),
  eggplant: eggplant,
  fadedEggplant8: fadedColorWithWhite(eggplant, 0.08),
  fadedOffBlack72: fadedColorWithWhite(offBlack, 0.72),
  fadedOffBlack64: fadedColorWithWhite(offBlack, 0.64),
  fadedOffBlack50: fadedColorWithWhite(offBlack, 0.5),
  fadedOffBlack32: fadedColorWithWhite(offBlack, 0.32),
  fadedOffBlack16: fadedColorWithWhite(offBlack, 0.16),
  fadedOffBlack8: fadedColorWithWhite(offBlack, 0.08)
});

const font = {
  family: {
    sans: 'Lato, "Noto Sans", sans-serif',
    serif: '"Noto Serif", serif',
    mono: "Inconsolata, monospace"
  },
  size: {
    xxxLarge: 36,
    xxLarge: 28,
    xLarge: 24,
    large: 20,
    medium: 16,
    small: 14,
    xSmall: 12
  },
  lineHeight: {
    xxxLarge: 40,
    xxLarge: 32,
    xLarge: 28,
    large: 24,
    medium: 20,
    small: 18,
    xSmall: 16
  },
  weight: {
    light: 300,
    regular: 400,
    bold: 700
  }
};

const spacing = {
  xxxxSmall_2: 2,
  xxxSmall_4: 4,
  xxSmall_6: 6,
  xSmall_8: 8,
  small_12: 12,
  medium_16: 16,
  large_24: 24,
  xLarge_32: 32,
  xxLarge_48: 48,
  xxxLarge_64: 64
};

const width = {
  xsMax: 567,
  smMin: 568,
  smMax: 681,
  mdMin: 682,
  mdMax: 1023,
  lgMin: 1024
};
const mediaQuery = {
  xs: `@media screen and (max-width: ${width.xsMax}px) /* breakpoint.mediaQuery.xs */`,
  sm: `@media screen and (min-width: ${width.smMin}px) and (max-width: ${width.smMax}px) /* breakpoint.mediaQuery.sm */`,
  md: `@media screen and (min-width: ${width.mdMin}px) and (max-width: ${width.mdMax}px) /* breakpoint.mediaQuery.md */`,
  lg: `@media screen and (min-width: ${width.mdMin}px) and (max-width: ${width.lgMin}px) /* breakpoint.mediaQuery.lg */`,
  xl: `@media screen and (min-width: ${width.lgMin}px) /* breakpoint.mediaQuery.xl */`,
  xsOrSmaller: `@media screen and (max-width: ${width.xsMax}px) /* breakpoint.mediaQuery.xsOrSmaller */`,
  smOrSmaller: `@media screen and (max-width: ${width.smMax}px) /* breakpoint.mediaQuery.smOrSmaller */`,
  mdOrSmaller: `@media screen and (max-width: ${width.mdMax}px) /* breakpoint.mediaQuery.mdOrSmaller */`,
  lgOrSmaller: `@media screen and (max-width: ${width.lgMin}px) /* breakpoint.mediaQuery.lgOrSmaller */`,
  smOrLarger: `@media screen and (min-width: ${width.smMin}px) /* breakpoint.mediaQuery.smOrLarger */`,
  mdOrLarger: `@media screen and (min-width: ${width.mdMin}px) /* breakpoint.mediaQuery.mdOrLarger */`,
  lgOrLarger: `@media screen and (min-width: ${width.lgMin}px) /* breakpoint.mediaQuery.lgOrLarger */`
};
const breakpoint = {
  width,
  mediaQuery
};

const CSS_VAR_PREFIX = "--wb-";

function mapValuesToCssVars(obj, prefix = "--wb-") {
  const cssVars = {};
  for (const key in obj) {
    if (typeof obj[key] === "object") {
      cssVars[key] = mapValuesToCssVars(obj[key], `${prefix}${key}-`);
    } else {
      cssVars[key] = `var(${prefix}${key})`;
    }
  }
  return cssVars;
}

const baseline = 10;
function pxToRem(value) {
  return `${value / baseline}rem`;
}
function remToPx(value) {
  const num = parseFloat(value);
  return `${Math.round(num * baseline)}px`;
}
const sizing$1 = {
  size_0: pxToRem(0),
  size_010: pxToRem(1),
  size_020: pxToRem(2),
  size_040: pxToRem(4),
  size_060: pxToRem(6),
  size_080: pxToRem(8),
  size_100: pxToRem(10),
  size_120: pxToRem(12),
  size_140: pxToRem(14),
  size_160: pxToRem(16),
  size_180: pxToRem(18),
  size_200: pxToRem(20),
  size_240: pxToRem(24),
  size_280: pxToRem(28),
  size_320: pxToRem(32),
  size_360: pxToRem(36),
  size_400: pxToRem(40),
  size_480: pxToRem(48),
  size_560: pxToRem(56),
  size_640: pxToRem(64),
  size_720: pxToRem(72),
  size_800: pxToRem(80),
  size_880: pxToRem(88),
  size_960: pxToRem(96)
};

const border$2 = {
  primary: color.fadedOffBlack16,
  subtle: color.fadedOffBlack8,
  strong: color.fadedOffBlack50,
  inverse: color.white
};
const semanticColor$1 = {
  action: {
    primary: {
      progressive: {
        default: {
          border: "transparent",
          background: color.blue,
          foreground: color.white
        },
        hover: {
          border: color.blue,
          background: color.blue,
          foreground: color.white
        },
        press: {
          border: color.activeBlue,
          background: color.activeBlue,
          foreground: color.white
        }
      },
      destructive: {
        default: {
          border: "transparent",
          background: color.red,
          foreground: color.white
        },
        hover: {
          border: color.red,
          background: color.red,
          foreground: color.white
        },
        press: {
          border: color.activeRed,
          background: color.activeRed,
          foreground: color.white
        }
      },
      neutral: {
        default: {
          border: "transparent",
          background: color.fadedOffBlack72,
          foreground: color.white
        },
        hover: {
          border: color.fadedOffBlack72,
          background: color.fadedOffBlack72,
          foreground: color.white
        },
        press: {
          border: color.offBlack,
          background: color.offBlack,
          foreground: color.white
        }
      },
      disabled: {
        border: color.fadedOffBlack32,
        background: color.fadedOffBlack32,
        foreground: color.offWhite
      }
    },
    secondary: {
      progressive: {
        default: {
          border: border$2.strong,
          background: "transparent",
          foreground: color.blue
        },
        hover: {
          border: color.blue,
          background: "transparent",
          foreground: color.blue
        },
        press: {
          border: color.activeBlue,
          background: color.fadedBlue,
          foreground: color.activeBlue
        }
      },
      destructive: {
        default: {
          border: border$2.strong,
          background: "transparent",
          foreground: color.red
        },
        hover: {
          border: color.red,
          background: "transparent",
          foreground: color.red
        },
        press: {
          border: color.activeRed,
          background: color.fadedRed,
          foreground: color.activeRed
        }
      },
      neutral: {
        default: {
          border: border$2.strong,
          background: "transparent",
          foreground: color.fadedOffBlack72
        },
        hover: {
          border: color.fadedOffBlack72,
          background: "transparent",
          foreground: color.fadedOffBlack72
        },
        press: {
          border: color.offBlack,
          background: "transparent",
          foreground: color.offBlack
        }
      },
      disabled: {
        border: border$2.primary,
        background: color.offWhite,
        foreground: color.fadedOffBlack32
      }
    },
    tertiary: {
      progressive: {
        default: {
          border: "transparent",
          background: "transparent",
          foreground: color.blue
        },
        hover: {
          border: color.blue,
          background: "transparent",
          foreground: color.blue
        },
        press: {
          border: color.activeBlue,
          background: "transparent",
          foreground: color.activeBlue
        }
      },
      destructive: {
        default: {
          border: "transparent",
          background: "transparent",
          foreground: color.red
        },
        hover: {
          border: color.red,
          background: "transparent",
          foreground: color.red
        },
        press: {
          border: color.activeRed,
          background: "transparent",
          foreground: color.activeRed
        }
      },
      neutral: {
        default: {
          border: "transparent",
          background: "transparent",
          foreground: color.fadedOffBlack72
        },
        hover: {
          border: color.fadedOffBlack72,
          background: "transparent",
          foreground: color.fadedOffBlack72
        },
        press: {
          border: color.offBlack,
          background: "transparent",
          foreground: color.offBlack
        }
      },
      disabled: {
        border: border$2.primary,
        background: "transparent",
        foreground: color.fadedOffBlack32
      }
    }
  },
  status: {
    critical: {
      background: color.fadedRed8,
      foreground: color.red
    },
    warning: {
      background: color.fadedGold8,
      foreground: color.gold
    },
    success: {
      background: color.fadedGreen8,
      foreground: color.green
    },
    notice: {
      background: color.fadedBlue8,
      foreground: color.blue
    },
    neutral: {
      background: color.fadedOffBlack8,
      foreground: color.offBlack
    }
  },
  surface: {
    primary: color.white,
    secondary: color.offWhite,
    emphasis: color.blue,
    inverse: color.darkBlue,
    overlay: color.offBlack64
  },
  text: {
    primary: color.offBlack,
    secondary: color.fadedOffBlack72,
    disabled: color.fadedOffBlack32,
    inverse: color.white
  },
  border: border$2,
  focus: {
    outer: color.blue,
    inner: color.white
  },
  icon: {
    primary: color.fadedOffBlack72,
    secondary: color.offBlack,
    inverse: color.white,
    action: color.blue,
    destructive: color.red,
    disabled: color.fadedOffBlack32
  },
  khanmigo: {
    primary: color.eggplant,
    secondary: color.fadedEggplant8
  },
  mastery: {
    primary: color.purple
  }
};

const border$1 = {
  radius: {
    radius_0: remToPx(sizing$1.size_0),
    radius_040: remToPx(sizing$1.size_040),
    radius_080: remToPx(sizing$1.size_080),
    radius_120: remToPx(sizing$1.size_120),
    radius_full: "50%"
  },
  width: {
    none: remToPx(sizing$1.size_0),
    thin: remToPx(sizing$1.size_010),
    medium: remToPx(sizing$1.size_020),
    thick: remToPx(sizing$1.size_040)
  }
};

var themeDefault = {
  border: border$1,
  semanticColor: semanticColor$1,
  sizing: sizing$1
};

var theme = mapValuesToCssVars(themeDefault, CSS_VAR_PREFIX);

const {
  border,
  semanticColor,
  sizing
} = theme;

export { border, breakpoint, color, fade, font, mix, semanticColor, sizing, spacing };
